import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useToast,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Center,
} from '@chakra-ui/react';
import {
  AddIcon,
  SearchIcon,
  ViewIcon,
  EditIcon,
  DownloadIcon,
  EmailIcon,
} from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/apiUrl';
import { getProposal } from '../services/proposalManagement';
import { downloadProposalPDF, generateAndUploadProposalPDF } from '../services/pdfService';
import { updateProposal } from '../services/proposalManagement';

interface Proposal {
  id: string;
  leadId: string;
  leadName: string;
  companyName: string;
  doorCount: number;
  planTier: 'starter' | 'professional' | 'enterprise';
  monthlyPrice: number;
  annualPrice: number;
  discount: number;
  totalValue: number;
  status: 'draft' | 'sent' | 'viewed' | 'accepted' | 'declined';
  sentDate?: string;
  viewedDate?: string;
  acceptedDate?: string;
  declinedDate?: string;
  viewCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface ProposalStats {
  totalProposals: number;
  sent: number;
  accepted: number;
  declined: number;
  conversionRate: number;
  avgValue: number;
}

const ProposalsPage: React.FC = () => {
  const { t } = useTranslation('proposals');
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [stats, setStats] = useState<ProposalStats>({
    totalProposals: 0,
    sent: 0,
    accepted: 0,
    declined: 0,
    conversionRate: 0,
    avgValue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchProposals();
    fetchStats();
  }, []);

  const fetchProposals = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/getProposals?status=${statusFilter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch proposals');

      const data = await response.json();
      setProposals(data.proposals || []);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast({
        title: t('errors.fetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/getProposalStats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data.stats || stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSendProposal = async (proposalId: string) => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/sendProposal`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ proposalId }),
        }
      );

      if (!response.ok) throw new Error('Failed to send proposal');

      toast({
        title: t('success.sent'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchProposals();
      fetchStats();
    } catch (error) {
      toast({
        title: t('errors.sendFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDownloadPDF = async (proposalId: string) => {
    try {
      const proposal = await getProposal(proposalId);
      
      // Check if PDF already exists
      if (proposal.pdfUrl) {
        // Download existing PDF
        const link = document.createElement('a');
        link.href = proposal.pdfUrl;
        link.download = `${proposal.title.replace(/[^a-z0-9]/gi, '_')}_${proposalId}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast({
          title: 'PDF download started',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Generate and download PDF
        toast({
          title: 'Generating PDF...',
          status: 'info',
          duration: 2000,
          isClosable: true,
        });

        await downloadProposalPDF(proposal);
        
        // Optionally upload to storage for future use
        const pdfUrl = await generateAndUploadProposalPDF(proposal);
        
        if (!user) {
          throw new Error('User not authenticated');
        }
        
        // Update proposal with PDF URL (temporary workaround)
        const proposalRef = await import('firebase/firestore').then(m => m.doc(
          m.getFirestore(),
          'proposals',
          proposalId
        ));
        await import('firebase/firestore').then(m => m.updateDoc(proposalRef, { pdfUrl }));

        toast({
          title: 'PDF downloaded successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: 'Failed to download PDF',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'gray';
      case 'sent':
        return 'blue';
      case 'viewed':
        return 'purple';
      case 'accepted':
        return 'green';
      case 'declined':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'starter':
        return 'blue';
      case 'professional':
        return 'purple';
      case 'enterprise':
        return 'pink';
      default:
        return 'gray';
    }
  };

  const filteredProposals = proposals.filter((proposal) => {
    const matchesSearch =
      searchTerm === '' ||
      proposal.leadName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      proposal.companyName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' || proposal.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">{t('title')}</Heading>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="brand"
          onClick={() => navigate('/admin/proposals/new')}
        >
          {t('createNew')}
        </Button>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>{t('stats.total')}</StatLabel>
              <StatNumber>{stats.totalProposals}</StatNumber>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>{t('stats.sent')}</StatLabel>
              <StatNumber>{stats.sent}</StatNumber>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>{t('stats.accepted')}</StatLabel>
              <StatNumber color="green.500">{stats.accepted}</StatNumber>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>{t('stats.conversionRate')}</StatLabel>
              <StatNumber>{stats.conversionRate}%</StatNumber>
              <StatHelpText>{t('stats.conversionHelp')}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>{t('stats.avgValue')}</StatLabel>
              <StatNumber>${stats.avgValue.toLocaleString()}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Card mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">{t('filters.allStatuses')}</option>
              <option value="draft">{t('filters.draft')}</option>
              <option value="sent">{t('filters.sent')}</option>
              <option value="viewed">{t('filters.viewed')}</option>
              <option value="accepted">{t('filters.accepted')}</option>
              <option value="declined">{t('filters.declined')}</option>
            </Select>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Proposals Table */}
      <Card>
        <CardBody>
          {loading ? (
            <Center py={10}>
              <Spinner size="xl" />
            </Center>
          ) : filteredProposals.length === 0 ? (
            <Center py={10}>
              <Text color="gray.500">{t('noProposals')}</Text>
            </Center>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>{t('table.lead')}</Th>
                    <Th>{t('table.tier')}</Th>
                    <Th isNumeric>{t('table.doors')}</Th>
                    <Th isNumeric>{t('table.value')}</Th>
                    <Th isNumeric>{t('table.discount')}</Th>
                    <Th>{t('table.status')}</Th>
                    <Th>{t('table.sentDate')}</Th>
                    <Th isNumeric>{t('table.views')}</Th>
                    <Th>{t('table.actions')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredProposals.map((proposal) => (
                    <Tr key={proposal.id}>
                      <Td>
                        <Box>
                          <Text fontWeight="medium">{proposal.leadName}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {proposal.companyName}
                          </Text>
                        </Box>
                      </Td>
                      <Td>
                        <Badge colorScheme={getTierColor(proposal.planTier)}>
                          {proposal.planTier}
                        </Badge>
                      </Td>
                      <Td isNumeric>{proposal.doorCount}</Td>
                      <Td isNumeric>
                        ${proposal.totalValue.toLocaleString()}
                        <Text fontSize="xs" color="gray.500">
                          ${proposal.monthlyPrice.toLocaleString()}/mo
                        </Text>
                      </Td>
                      <Td isNumeric>
                        {proposal.discount > 0 ? (
                          <Badge colorScheme="green">{proposal.discount}%</Badge>
                        ) : (
                          '-'
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(proposal.status)}>
                          {proposal.status}
                        </Badge>
                      </Td>
                      <Td>
                        {proposal.sentDate
                          ? new Date(proposal.sentDate).toLocaleDateString()
                          : '-'}
                      </Td>
                      <Td isNumeric>
                        {proposal.viewCount > 0 ? (
                          <Badge>{proposal.viewCount}</Badge>
                        ) : (
                          '-'
                        )}
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<ViewIcon />}
                            size="sm"
                            variant="ghost"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<ViewIcon />}
                              onClick={() =>
                                navigate(`/admin/proposals/${proposal.id}`)
                              }
                            >
                              {t('actions.view')}
                            </MenuItem>
                            {proposal.status === 'draft' && (
                              <MenuItem
                                icon={<EditIcon />}
                                onClick={() =>
                                  navigate(`/admin/proposals/${proposal.id}/edit`)
                                }
                              >
                                {t('actions.edit')}
                              </MenuItem>
                            )}
                            {proposal.status === 'draft' && (
                              <MenuItem
                                icon={<EmailIcon />}
                                onClick={() => handleSendProposal(proposal.id)}
                              >
                                {t('actions.send')}
                              </MenuItem>
                            )}
                            <MenuItem
                              icon={<DownloadIcon />}
                              onClick={() => handleDownloadPDF(proposal.id)}
                            >
                              {t('actions.download')}
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default ProposalsPage;
